/**
 * chatReducer — pure conversation state machine for the WBR Callouts Viewer
 * chatbox.
 *
 * This module defines the conversation types and the reducer that drives the
 * chatbox's conversation history, conversation id, team binding, and pending
 * status. The reducer is pure: it never performs I/O and never mutates its
 * inputs — every transition returns new objects/arrays.
 */

import type { ChartSpec } from "../../answerChart/types";
import {
  DEFAULT_ERROR_TEXT,
  NO_ANSWER_CONTENT_TEXT,
  TIMEOUT_TEXT,
} from "./chatConstants";

/** Role of a single chat message. */
export type Role = "user" | "assistant";

/** A parsed source reference associated with an assistant message. */
export type Source = {
  label: string;
  detail?: string;
};

/** A single entry in the conversation history. */
export interface ChatMessage {
  /** Stable client id (caller-supplied, deterministic in tests). */
  id: string;
  role: Role;
  text: string;
  /** Present only on assistant messages that carry source references. */
  sources?: Source[];
  /** Chart specs from the create_chart tool, rendered under the answer. */
  charts?: ChartSpec[];
  /** True for error / timeout / empty-response notices (assistant-side). */
  isError?: boolean;
}

/** In-memory conversation state (component lifetime only). */
export interface ChatState {
  /** Conversation history, in chronological order. */
  messages: ChatMessage[];
  /** Active conversation id; null means no active conversation. */
  conversationId: string | null;
  /** Team id the conversation is scoped to; null means unbound. */
  boundTeamId: number | null;
  /** Whether a mutation is in flight. */
  status: "idle" | "pending";
}

/** Actions that drive the conversation state machine. */
export type ChatAction =
  // Append the user's question, transition to pending. The optional
  // `conversationId` lets the client own a stable id from the first turn so
  // follow-ups reuse it even if the backend response omits it.
  | { type: "SUBMIT"; messageId: string; text: string; conversationId?: string }
  // Append the assistant's reply and (when non-empty) store the conversation id.
  | {
      type: "SUCCESS";
      messageId: string;
      response: string;
      conversationId: string;
      sources: Source[];
      charts?: ChartSpec[];
    }
  // Append a "no answer content" notice; retain the user message.
  | { type: "EMPTY_RESPONSE"; messageId: string }
  // Append an error notice; retain prior messages.
  | { type: "FAILURE"; messageId: string; message: string }
  // Append a timeout notice; retain prior messages.
  | { type: "TIMEOUT"; messageId: string }
  // Clear conversation id and history.
  | { type: "NEW_CONVERSATION" }
  // Clear conversation id and history, rebind to the new team.
  | { type: "TEAM_CHANGED"; teamId: number };

/** Initial conversation state. */
export const initialState: ChatState = {
  messages: [],
  conversationId: null,
  boundTeamId: null,
  status: "idle",
};

/**
 * Pure conversation reducer.
 *
 * Drives the conversation state machine. Every branch returns a new state
 * object (with new `messages` arrays/objects) rather than mutating the inputs,
 * keeping the reducer pure and safe for property-based testing.
 */
export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SUBMIT": {
      // Append the user's question and transition to pending (Req 5.1).
      const userMessage: ChatMessage = {
        id: action.messageId,
        role: "user",
        text: action.text,
      };
      // Adopt a client-supplied conversation id on the first turn so it stays
      // stable across follow-ups; never overwrite an already-established id.
      const conversationId =
        state.conversationId ??
        (typeof action.conversationId === "string" &&
        action.conversationId.length > 0
          ? action.conversationId
          : null);
      return {
        ...state,
        messages: [...state.messages, userMessage],
        conversationId,
        status: "pending",
      };
    }

    case "SUCCESS": {
      // Append the assistant's reply; return to idle (Req 5.3).
      const assistantMessage: ChatMessage = {
        id: action.messageId,
        role: "assistant",
        text: action.response,
        sources: action.sources,
        charts: action.charts,
      };
      // Store the returned conversation id only when it is a non-empty string
      // (Req 3.2); leave the prior id unchanged when missing/empty (Req 3.3).
      const conversationId =
        typeof action.conversationId === "string" &&
        action.conversationId.length > 0
          ? action.conversationId
          : state.conversationId;
      return {
        ...state,
        messages: [...state.messages, assistantMessage],
        conversationId,
        status: "idle",
      };
    }

    case "EMPTY_RESPONSE": {
      // Show a "no answer content" notice; retain the user message (Req 4.5).
      const notice: ChatMessage = {
        id: action.messageId,
        role: "assistant",
        text: NO_ANSWER_CONTENT_TEXT,
        isError: true,
      };
      return {
        ...state,
        messages: [...state.messages, notice],
        status: "idle",
      };
    }

    case "FAILURE": {
      // Show the server-provided message when meaningful, else the default
      // error text (Req 6.3, 6.4); retain prior messages (Req 6.5).
      const text =
        typeof action.message === "string" && action.message.trim().length > 0
          ? action.message
          : DEFAULT_ERROR_TEXT;
      const errorMessage: ChatMessage = {
        id: action.messageId,
        role: "assistant",
        text,
        isError: true,
      };
      return {
        ...state,
        messages: [...state.messages, errorMessage],
        status: "idle",
      };
    }

    case "TIMEOUT": {
      // Show a timeout notice; retain prior messages (Req 5.5, 6.2, 6.5).
      const timeoutMessage: ChatMessage = {
        id: action.messageId,
        role: "assistant",
        text: TIMEOUT_TEXT,
        isError: true,
      };
      return {
        ...state,
        messages: [...state.messages, timeoutMessage],
        status: "idle",
      };
    }

    case "NEW_CONVERSATION": {
      // Clear history and conversation id; keep the team binding (Req 3.5, 3.6).
      return {
        ...state,
        messages: [],
        conversationId: null,
        status: "idle",
      };
    }

    case "TEAM_CHANGED": {
      // Reset the conversation and rebind to the new team (Req 7.5).
      return {
        ...state,
        messages: [],
        conversationId: null,
        boundTeamId: action.teamId,
        status: "idle",
      };
    }

    default:
      return state;
  }
}
